// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteUser as deleteUserRequest } from '../services/userService';

export interface DeleteUserView {
  deleteUser: (userId: string) => Promise<void>;
  isDeleting: boolean;
}

/**
 * Wraps `deleteUser()` (`DELETE /api/v1/users/{id}`) with cache invalidation,
 * so Admin Center's Users screen doesn't reach into `shared/services/`
 * directly (ADR-0014). Invalidates the same `['admin-center', 'users']`
 * query key `useUsers` reads, so a live list (ADO #22692) picks up the
 * removal without a manual refetch.
 *
 * No `error` field: the one caller (`UsersPage`) renders a message naming
 * the user being deleted, which this hook has no way to produce — it
 * catches `deleteUser()`'s rejection itself instead of reading mutation
 * state here. Add one back only once a second caller needs a generic
 * message.
 */
export function useDeleteUser(): DeleteUserView {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (userId: string) => deleteUserRequest(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-center', 'users'] });
    },
  });

  return {
    deleteUser: userId => mutation.mutateAsync(userId),
    isDeleting: mutation.isPending,
  };
}
